/**
 * The release page.
 *
 * Design Bible 4.2 is explicit that the Slab Frame belongs to the
 * `PhysicalItem`, not the `Product`, and that Product pages use plain editorial
 * cards — the frame is what teaches the difference between "this release" and
 * "this object". This page is the other half of that lesson: it names the
 * release once, in the editorial voice, and then lists the exact copies with
 * their own frames, so the identity levels in MASTER_SPEC 0.5.2 are visible
 * rather than explained.
 *
 * Copies can be split across channels, and that is shown honestly: an
 * eBay-owned copy sits beside a website copy with its own availability chip,
 * because pretending the catalogue is single-channel is what makes a `Buy now`
 * button lie later.
 */

import type { ReactNode } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { CatalogCard } from "./CatalogView.js";
import { Quoted } from "../components/primitives.js";
import { EmptyBlock, ResourceGate } from "../components/states.js";
import { productPath } from "../lib/coreRoutes.js";
import { parseItemList, type PublicItem } from "../lib/publicContract.js";
import { useResource } from "../lib/useResource.js";

export function ProductView({ productID }: { productID: string }): ReactNode {
  const { transport, preload } = useStorefront();
  const path = productPath(productID);
  const initial = preload !== null && preload.path === path ? (preload.items ?? null) : null;

  const resource = useResource<readonly PublicItem[]>(transport, {
    path,
    parse: parseItemList,
    requirement: "this release",
    initial
  });

  return (
    <ResourceGate resource={resource} skeletonCards={2} subject="release" waitingFor="this release">
      {(items) => <ProductDetail items={items} />}
    </ResourceGate>
  );
}

function ProductDetail({ items }: { items: readonly PublicItem[] }): ReactNode {
  const first = items[0];
  if (first === undefined) {
    return (
      <EmptyBlock
        action={
          <a className="btn btn--secondary" href="/catalog">
            Back to the catalogue
          </a>
        }
        statement="No copies of this release are listed right now."
      />
    );
  }

  return (
    <div className="stack product">
      {/* A div, not a header: only the shell's masthead is the banner landmark. */}
      <div className="product-head">
        <h1 className="t-display-l">{first.title}</h1>
        <p className="t-body-l t-secondary">
          {first.platform} · {first.region}
          {first.publisher === undefined ? null : <> · {first.publisher}</>}
        </p>
        {first.description === undefined ? (
          <p className="t-body-m t-secondary">
            No approved description has been published for this release yet.
          </p>
        ) : (
          <Quoted label="Approved release description" text={first.description} />
        )}
      </div>

      <section aria-labelledby="copies-title" className="stack">
        <h2 className="t-display-m" id="copies-title">
          {items.length === 1 ? "One copy listed" : `${String(items.length)} copies listed`}
        </h2>
        <p className="t-body-m">
          Each copy below is a distinct object with its own grade, slab condition, photographs, and
          price. They are not interchangeable.
        </p>
        <ul className="catalog-grid">
          {items.map((item) => (
            <li key={item.physical_item_id}>
              <CatalogCard item={item} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
